import React, { useEffect, useState } from 'react';
import { Input } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { NewContractNegotiationConcept } from '../../../models/Entities/Contracts/NewContractNegotiationConcept';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';

interface Props {
  contractNegotiationId: string;
  contractId: number;
  concept?: ContractNegotiationConcept;
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (entity: NewContractNegotiationConcept | ContractNegotiationConcept) => void;
}

const ContractNegotiationConceptFormComponent = (props: Props) => {
  const { t } = useTranslation();

  const [conceptId, setConceptId] = useState<number>(0);
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [negotiationReferenceCost, setNegotiationReferenceCost] = useState<number>(0);
  const [baselineVariance, setBaselineVariance] = useState<number>(0);

  useEffect(() => {
    if (props.isOpen) {
      setConceptId(props.concept?.conceptId ?? 0);
      setCurrencyCode(props.concept?.currencyCode || '');
      setNegotiationReferenceCost(
        typeof props.concept?.negotiationReferenceCost === 'number'
          ? props.concept.negotiationReferenceCost
          : 0
      );
      setBaselineVariance(
        typeof props.concept?.baselineVariance === 'number' ? props.concept.baselineVariance : 0
      );
    }
  }, [props.isOpen, props.concept]);

  const handleSave = () => {
    const base: NewContractNegotiationConcept = {
      contractNegotiationId: props.contractNegotiationId,
      conceptId,
      currencyCode: currencyCode || undefined,
      negotiationReferenceCost,
      baselineVariance,
    };

    if (props.concept?.id) {
      props.onSave({ ...props.concept, ...base });
    } else {
      props.onSave(base);
    }
  };

  const isEdit = !!props.concept?.id;
  const dialogTitle = isEdit
    ? t('contractNegotiationConceptForm.titleEdit', 'Editar concepto de negociación')
    : t('contractNegotiationConceptForm.titleNew', 'Nuevo concepto de negociación');

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      title={dialogTitle}
      onDismiss={props.onDismiss}
      testId="contract-negotiation-concept-drawer"
      secondaryAction={{ label: t('contractNegotiationConceptForm.actions.cancel', 'Cancelar') }}
      primaryAction={{
        label: t('contractNegotiationConceptForm.actions.save', 'Guardar'),
        onClick: handleSave,
      }}
    >
      <GuideDrawerForm onSubmit={handleSave} testId="contract-negotiation-concept-form">
        <GuideFieldGrid>
          <GuideField label={t('contractNegotiationConceptForm.fields.conceptId', 'ID Concepto')}>
            <Input
              type="number"
              value={String(conceptId)}
              onChange={(_, data) => setConceptId(Number(data.value))}
            />
          </GuideField>
          <GuideField label={t('contractNegotiationConceptForm.fields.currencyCode', 'Código de moneda')}>
            <Input
              value={currencyCode}
              onChange={(_, data) => setCurrencyCode(data.value)}
              placeholder="ARS"
            />
          </GuideField>
        </GuideFieldGrid>
        <GuideFieldGrid>
          <GuideField label={t('contractNegotiationConceptForm.fields.referenceCost', 'Costo de referencia')}>
            <Input
              type="number"
              value={String(negotiationReferenceCost)}
              onChange={(_, data) => setNegotiationReferenceCost(Number(data.value))}
            />
          </GuideField>
          <GuideField label={t('contractNegotiationConceptForm.fields.baselineVariance', 'Varianza de base (%)')}>
            <Input
              type="number"
              value={String(baselineVariance)}
              onChange={(_, data) => setBaselineVariance(Number(data.value))}
            />
          </GuideField>
        </GuideFieldGrid>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default ContractNegotiationConceptFormComponent;
